import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
  Stack,
} from '@mui/material';
import { Add, Edit, Delete, PersonAdd } from '@mui/icons-material';
import {
  useGrupoFamiliar,
  useCreateMiembroFamiliar,
  useUpdateMiembroFamiliar,
  useDeleteMiembroFamiliar,
  useParentescos,
} from '../../../hooks/useGrupoFamiliar';
import { formatDate } from '../../../utils/format';
import type { MiembroFamiliar } from '../../../types/afiliado';

interface Props {
  afiliadoId: string;
}

interface FormState {
  open: boolean;
  editing: MiembroFamiliar | null;
  nombre: string;
  apellido: string;
  dni: string;
  parentesco_id: string;
  fecha_nacimiento: string;
  obra_social: string;
  observaciones: string;
}

const emptyForm: FormState = {
  open: false,
  editing: null,
  nombre: '',
  apellido: '',
  dni: '',
  parentesco_id: '',
  fecha_nacimiento: '',
  obra_social: '',
  observaciones: '',
};

export default function TabGrupoFamiliar({ afiliadoId }: Props) {
  const { data: familiares = [], isLoading } = useGrupoFamiliar(afiliadoId);
  const { data: parentescos = [] } = useParentescos();
  const crear = useCreateMiembroFamiliar();
  const editar = useUpdateMiembroFamiliar();
  const eliminar = useDeleteMiembroFamiliar();

  const [form, setForm] = useState<FormState>(emptyForm);

  const openCreate = () => setForm({ ...emptyForm, open: true });

  const openEdit = (m: MiembroFamiliar) =>
    setForm({
      open: true,
      editing: m,
      nombre: m.nombre,
      apellido: m.apellido,
      dni: m.dni ?? '',
      parentesco_id: m.parentesco_id ?? '',
      fecha_nacimiento: m.fecha_nacimiento ?? '',
      obra_social: m.obra_social ?? '',
      observaciones: m.observaciones ?? '',
    });

  const close = () => setForm(emptyForm);

  const handleSubmit = async () => {
    const payload = {
      afiliado_id: afiliadoId,
      nombre: form.nombre.trim(),
      apellido: form.apellido.trim(),
      dni: form.dni.trim() || null,
      parentesco_id: form.parentesco_id || null,
      fecha_nacimiento: form.fecha_nacimiento || null,
      obra_social: form.obra_social.trim() || null,
      observaciones: form.observaciones.trim() || null,
    };
    if (form.editing) {
      await editar.mutateAsync({ id: form.editing.id, payload });
    } else {
      await crear.mutateAsync(payload);
    }
    close();
  };

  const handleDelete = async (m: MiembroFamiliar) => {
    if (!confirm(`Eliminar a ${m.nombre} ${m.apellido} del grupo familiar?`)) return;
    await eliminar.mutateAsync({ id: m.id, afiliado_id: afiliadoId });
  };

  const isSubmitting = crear.isPending || editar.isPending;
  const canSubmit = form.nombre.trim() && form.apellido.trim();

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Informacion opcional pero util: permite distribuir utiles escolares,
        cajas navidenas, beneficios sindicales por familia, etc.
      </Alert>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Familiares registrados ({familiares.length})
        </Typography>
        <Button variant="outlined" startIcon={<Add />} onClick={openCreate} size="small">
          Agregar familiar
        </Button>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : familiares.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.secondary',
          }}
        >
          <PersonAdd sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="body2">No hay familiares cargados</Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre completo</TableCell>
              <TableCell>DNI</TableCell>
              <TableCell>Parentesco</TableCell>
              <TableCell>Fecha nac.</TableCell>
              <TableCell>Obra social</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {familiares.map((m) => {
              const parentesco = parentescos.find((p) => p.id === m.parentesco_id);
              return (
                <TableRow key={m.id} hover>
                  <TableCell>
                    <strong>{m.apellido}</strong>, {m.nombre}
                  </TableCell>
                  <TableCell>{m.dni || '—'}</TableCell>
                  <TableCell>{parentesco?.descripcion || '—'}</TableCell>
                  <TableCell>{formatDate(m.fecha_nacimiento)}</TableCell>
                  <TableCell>{m.obra_social || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEdit(m)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => handleDelete(m)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={form.open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{form.editing ? 'Editar familiar' : 'Agregar familiar'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                required
                fullWidth
                label="Apellido"
                value={form.apellido}
                onChange={(e) => setForm({ ...form, apellido: e.target.value })}
              />
              <TextField
                required
                fullWidth
                label="Nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="DNI"
                value={form.dni}
                onChange={(e) => setForm({ ...form, dni: e.target.value })}
              />
              <TextField
                fullWidth
                label="Fecha de nacimiento"
                type="date"
                value={form.fecha_nacimiento}
                onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              select
              fullWidth
              label="Parentesco"
              value={form.parentesco_id}
              onChange={(e) => setForm({ ...form, parentesco_id: e.target.value })}
            >
              <MenuItem value="">
                <em>Sin especificar</em>
              </MenuItem>
              {parentescos.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.descripcion}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Obra social"
              value={form.obra_social}
              onChange={(e) => setForm({ ...form, obra_social: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Observaciones"
              value={form.observaciones}
              onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!canSubmit || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {form.editing ? 'Guardar cambios' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
